import { Database } from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable, TableSettingsMenu } from '@@/datatables';
import {
  createPersistedStore,
  BasicTableSettings,
  RefreshableTableSettings,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';

import { useCustomResources } from '../queries/useCustomResources';
import { useCustomResourceDefinitions } from '../queries/useCustomResourceDefinitions';
import { useDeleteCustomResource } from '../queries/useDeleteCustomResource';
import { CustomResource } from '../types';

import { CustomResourceDefinitionFilter } from './CustomResourceDefinitionFilter';
import { createColumns } from './columns';

const storageKey = 'customResources';

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {
  selectedDefinition: string;
  setSelectedDefinition: (definition: string) => void;
}

const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'name',
  (set) => ({
    ...refreshableSettings(set),
    selectedDefinition: '',
    setSelectedDefinition: (selectedDefinition: string) =>
      set({ selectedDefinition }),
  })
);

interface Props {
  onDefinitionChange?: (definition: string) => void;
}

export function CustomResourcesDatatable({ onDefinitionChange }: Props) {
  const environmentId = useEnvironmentId();
  const tableState = useTableState(settingsStore, storageKey);

  // Fetch CRDs locally - useQuery cache will prevent refetching
  const { data: crds, isLoading: isLoadingCrds } = useCustomResourceDefinitions(
    environmentId,
    {
      refetchInterval: tableState.autoRefreshRate
        ? tableState.autoRefreshRate * 1000
        : undefined,
    }
  );

  // Get the selected CRD for determining if it's namespaced
  const selectedDefinition = crds?.find(
    (c) => c.name === tableState.selectedDefinition
  );
  const selectedDefinitionString = selectedDefinition
    ? tableState.selectedDefinition
    : '';
  const isNamespaced = selectedDefinition?.scope === 'Namespaced';

  // Fetch custom resources
  const { data: customResources, isLoading: isLoadingResources } =
    useCustomResources(environmentId, selectedDefinitionString, {
      refetchInterval: tableState.autoRefreshRate
        ? tableState.autoRefreshRate * 1000
        : undefined,
    });
  const customResourcesData = selectedDefinitionString
    ? customResources || []
    : [];

  const columns = createColumns(isNamespaced);

  // Show loading when CRDs are loading or when we have a selected definition and custom resources are loading
  const shouldShowLoading =
    isLoadingCrds || (!!selectedDefinitionString && isLoadingResources);

  const deleteMutation = useDeleteCustomResource(
    environmentId,
    tableState.selectedDefinition
  );

  function handleDefinitionChange(definition: string) {
    tableState.setSelectedDefinition(definition);
    onDefinitionChange?.(definition);
  }

  function handleRemove(selectedItems: CustomResource[]) {
    const customResourceNames = selectedItems.map((item) => item.name);
    deleteMutation.mutate(customResourceNames);
  }

  return (
    <Datatable
      dataset={customResourcesData}
      columns={columns}
      settingsManager={tableState}
      isLoading={shouldShowLoading}
      title="Custom Resources"
      titleIcon={Database}
      getRowId={(row) => `${row.namespace || 'cluster'}-${row.name}`}
      data-cy="custom-resources-datatable"
      emptyContentLabel={
        !tableState.selectedDefinition
          ? 'Select a Custom Resource Definition to view its resources'
          : 'No custom resources found.'
      }
      renderTableActions={(selectedItems) => (
        <DeleteButton
          data-cy="custom-resources-removeButton"
          disabled={selectedItems.length === 0 || deleteMutation.isLoading}
          isLoading={deleteMutation.isLoading}
          confirmMessage="Do you want to remove the selected custom resource(s)?"
          onConfirmed={() => handleRemove(selectedItems)}
        />
      )}
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            value={tableState.autoRefreshRate}
            onChange={tableState.setAutoRefreshRate}
          />
        </TableSettingsMenu>
      )}
      description={
        <div className="w-full">
          <div className="min-w-[400px] float-left">
            <CustomResourceDefinitionFilter
              crds={crds || []}
              value={selectedDefinitionString}
              onChange={handleDefinitionChange}
            />
          </div>
        </div>
      }
    />
  );
}
